import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { encryptSubtask } from '../utils/encryption';
import { normalizeUrl } from '../utils/utils';
import { Loader2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableSubtaskItem from './SortableSubtaskItem';

const SubtaskList = ({ subtasks, setSubtasks, ideaId, user }) => {
  const [newSubtask, setNewSubtask] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubtaskUpdate = async (updatedSubtasks) => {
    if (!user?.uid || !ideaId) {
      console.error('Missing user or ideaId');
      return;
    }

    try {
      setLoading(true);
      
      // Encrypt subtasks before saving to Firebase
      const encryptedSubtasks = updatedSubtasks.map(subtask => 
        encryptSubtask(subtask, process.env.REACT_APP_ENCRYPTION_KEY)
      );

      const ideaRef = doc(db, 'users', user.uid, 'ideas', ideaId);
      await updateDoc(ideaRef, {
        subtasks: encryptedSubtasks,
        updatedAt: serverTimestamp()
      });
      
      setSubtasks(updatedSubtasks);
    } catch (error) {
      console.error('Error updating subtasks:', error);
      throw error; // Propagate error to parent
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubtask = async () => {
    if (newSubtask.trim()) {
      const updatedSubtasks = [...subtasks, { text: newSubtask.trim(), completed: false, link: '' }];
      await handleSubtaskUpdate(updatedSubtasks);
      setNewSubtask('');
    }
  };

  const handleDeleteSubtask = async (index) => {
    const updatedSubtasks = subtasks.filter((_, i) => i !== index);
    await handleSubtaskUpdate(updatedSubtasks);
  };

  const handleToggleComplete = async (index) => {
    const updatedSubtasks = subtasks.map((subtask, i) => 
      i === index ? { ...subtask, completed: !subtask.completed } : subtask
    );
    await handleSubtaskUpdate(updatedSubtasks);
  };

  const handleUpdateLink = async (index, link) => {
    // Normalize URL to ensure it has a protocol
    const normalizedLink = link ? normalizeUrl(link) : '';
    const updatedSubtasks = subtasks.map((subtask, i) => 
      i === index ? { ...subtask, link: normalizedLink } : subtask
    );
    await handleSubtaskUpdate(updatedSubtasks);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id.replace('subtask-', ''));
      const newIndex = parseInt(over.id.replace('subtask-', ''));

      const reorderedSubtasks = arrayMove(subtasks, oldIndex, newIndex);
      await handleSubtaskUpdate(reorderedSubtasks);
    }
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={subtasks.map((_, index) => `subtask-${index}`)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-2 mb-4">
            {subtasks.map((subtask, index) => {
              // Handle legacy string format or object format
              const normalizedSubtask = typeof subtask === 'string' 
                ? { text: subtask, completed: false, link: '' }
                : { text: subtask.text || '', completed: subtask.completed || false, link: subtask.link || '' };
              
              return (
                <SortableSubtaskItem
                  key={`subtask-${index}`}
                  index={index}
                  subtask={normalizedSubtask}
                  onToggleComplete={() => handleToggleComplete(index)}
                  onDelete={() => handleDeleteSubtask(index)}
                  onUpdateLink={handleUpdateLink}
                  loading={loading}
                />
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
      <div className="space-y-2">
        <input
          type="text"
          value={newSubtask}
          onChange={(e) => setNewSubtask(e.target.value)}
          placeholder="New TODO"
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
        />
        <button
          onClick={handleAddSubtask}
          disabled={loading}
          className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Adding...
            </>
          ) : (
            'Add TODO'
          )}
        </button>
      </div>
    </div>
  );
};

export default SubtaskList;